import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, catchError, of } from 'rxjs';
import { CalculatorTTMData } from '../../shared/models/calculator-ttm-data';
import { ConstantsService } from './constants.service';
import { LoggingService } from './logging.service';

/**
 * Service for managing calculator TTM data
 * Provides centralized state management using signals for reactive updates
 * Shared across all calculator components
 */
@Injectable({
  providedIn: 'root'
})
export class CalculatorDataService {
  // Signal to hold the current TTM data, accessible to all calculator components
  private ttmData = signal<CalculatorTTMData | null>(null);

  // Signal for loading state
  private isLoading = signal<boolean>(false);

  // Signal for error messages
  private errorMessage = signal<string>('');

  constructor(
    private http: HttpClient,
    private constantsSvc: ConstantsService,
    private logger: LoggingService
  ) {}

  /**
   * Get the current TTM data signal (read-only)
   */
  getTtmDataSignal() {
    return this.ttmData.asReadonly();
  }

  /**
   * Get the loading state signal (read-only)
   */
  getLoadingSignal() {
    return this.isLoading.asReadonly();
  }

  /**
   * Get the error message signal (read-only)
   */
  getErrorSignal() {
    return this.errorMessage.asReadonly();
  }

  /**
   * Fetch TTM data for a given ticker from the API
   * Updates the ttmData signal on success
   * @param ticker - Stock ticker symbol
   * @returns Observable of CalculatorTTMData
   */
  getTtmData(ticker: string): Observable<CalculatorTTMData> {
    if (!ticker || ticker.trim().length === 0) {
      this.errorMessage.set('Ticker is required');
      return of({} as CalculatorTTMData);
    }

    this.isLoading.set(true);
    this.errorMessage.set('');

    const apiUrl = `${this.constantsSvc.api_calculator}${ticker.toUpperCase()}`;

    return this.http.get<CalculatorTTMData>(apiUrl).pipe(
      tap((data) => {
        // Update the signal with the fetched data
        this.ttmData.set(data);
        this.isLoading.set(false);
        this.logger.info(`Loaded TTM data for ${ticker}:`, data);
      }),
      catchError((error) => {
        this.logger.error('Error fetching calculator TTM data:', error);
        this.errorMessage.set(`Failed to load TTM data for ${ticker}`);
        this.isLoading.set(false);
        this.ttmData.set(null);
        return of({} as CalculatorTTMData);
      })
    );
  }

  /**
   * Clear the current TTM data
   */
  clearTtmData(): void {
    this.ttmData.set(null);
    this.errorMessage.set('');
  }
}
